import { SchedulerData } from '../components/BasePage';
import { trackEvent } from '../lib/tracking';
import {
  productClickEvent,
  clickEvent,
  GabitoEventDefinitionsWithAuth,
} from '../lib/tracking/eventPayloadDefinitions';
import { StorefrontType, HeroType } from '../server/types';

export type EventParams = {
  campaignId: string;
  offerTypeId: string;
};

export type Node = { eventParams: EventParams; data: any };

const getIterableValues = (data: any) => {
  const values = Object.values(data);
  return values.reduce((acc: any[], value: any) => {
    if (Array.isArray(value)) {
      return acc.concat(value);
    }
    if (value && typeof value === 'object') {
      acc.push(value);
    }
    return acc;
  }, []);
};

const clone = <T>(object: T): T => JSON.parse(JSON.stringify(object));

/**
 * Breadth first, iterative insertion of href and insertMarket
 * attributes for all checkout buttons in a given scheduler
 * api resource.
 *
 * Will use the closest eventParams to determine the campaign id
 * and offer type id to be used in the checkout url.
 */
export const setCheckoutButtonHref = (
  page: any,
  makeCheckoutLink: (eventParams: EventParams) => string,
) => {
  const resultPage = clone(page);

  const toGoThrough: Node[] = resultPage.components.map((component: any) => ({
    data: component.attributes,
    eventParams: component.eventParams as EventParams,
  }));

  while (toGoThrough.length > 0) {
    const current = toGoThrough.pop() as Node;
    if (current.data?.name === 'checkout-button' && current.eventParams) {
      current.data.href = makeCheckoutLink(current.eventParams);
      current.data.insertMarket = false;
    }

    const additionalValues = getIterableValues(current.data);
    additionalValues.forEach((value: any) => {
      toGoThrough.push({
        eventParams: value.eventParams || current.eventParams,
        data: value,
      });
    });
  }

  return resultPage;
};

export type ExperimentClickOptions = {
  variant: string;
  country: string;
};

const addStorefrontButtonTracking = (
  storefront: StorefrontType,
  market: string,
  referralCode: string,
  options: GabitoEventDefinitionsWithAuth,
  experimentClickOptions?: ExperimentClickOptions,
): StorefrontType => {
  if (!storefront.attributes?.plans) {
    return storefront;
  }

  storefront.attributes.plans = storefront.attributes.plans?.map(plan => ({
    ...plan,
    button: {
      ...plan.button,
      onClick: ({ params: { offerTypeId, campaignId, position } }) => {
        trackEvent(
          productClickEvent(
            {
              market,
              component: 'storefront-plan',
              eventParams: {
                offerTypeId: offerTypeId || '',
                campaignId: campaignId || '',
              },
              link: plan.button?.href,
              position,
              referralCode,
            },
            options,
          ),
        );
        if (experimentClickOptions) {
          trackEvent(
            clickEvent(
              {
                variant: experimentClickOptions.variant,
                country: experimentClickOptions.country,
                context: 'referrals-receiver',
                label: 'continue-cta',
              },
              options,
            ),
          );
        }
      },
    },
  }));
  return storefront;
};

const addHeroButtonTracking = (
  hero: HeroType,
  market: string,
  referralCode: string,
  options: GabitoEventDefinitionsWithAuth,
  experimentClickOptions?: ExperimentClickOptions,
): HeroType => {
  if (!hero.attributes?.buttons) {
    return hero;
  }

  hero.attributes.buttons = hero.attributes?.buttons?.map(button => {
    if (button.name === 'checkout-button' && hero.eventParams) {
      button.onClick = () => {
        trackEvent(
          productClickEvent(
            {
              market,
              component: 'hero',
              link: button.href,
              eventParams: hero.eventParams as EventParams,
              position: 0,
              referralCode,
            },
            options,
          ),
        );
        if (experimentClickOptions) {
          trackEvent(
            clickEvent(
              {
                variant: experimentClickOptions.variant,
                country: experimentClickOptions.country,
                context: 'referrals-receiver',
                label: 'continue-cta',
              },
              options,
            ),
          );
        }
      };
    }
    return button;
  });
  return hero;
};

export const addCheckoutButtonTracking = (
  page: SchedulerData,
  market: string,
  referralCode: string,
  options: GabitoEventDefinitionsWithAuth,
  experimentClickOptions?: ExperimentClickOptions,
) => {
  page.components = page.components.map(component => {
    if (component.name === 'storefront') {
      return addStorefrontButtonTracking(
        component,
        market,
        referralCode,
        options,
        experimentClickOptions,
      );
    }
    if (component.name === 'hero') {
      return addHeroButtonTracking(
        component,
        market,
        referralCode,
        options,
        experimentClickOptions,
      );
    }
    return component;
  });
  return page;
};
